/**
 * MARKA İŞARETİ — "Kırık W" geometrisinin TEK KAYNAĞI.
 *
 * Yetkili kaynak: `docs/warmbreak-marka-sozlesmesi.md` §2 (geometri), §3 (dört
 * zemin varyantı), §4 (boy kuralları). Değerler oradan ALINDI, uydurulmadı.
 *
 * NEDEN KOD İÇİNDE GEOMETRİ: işaret üç yerde çizilir (web SVG bileşeni · mobil
 * `react-native-svg` · üretilen dosyalar: favicon, uygulama ikonu, e-posta PNG,
 * OG). Üçü ayrı yazılsaydı kontur kalınlığı ve kol açıları sessizce ayrışırdı.
 * `brand-asset.guard.spec.ts` depodaki ÜRETİLMİŞ SVG'leri burada hesaplanan
 * çıktıyla karşılaştırır: biri değişip diğeri değişmezse test KIRILIR.
 *
 * RENK BİLEŞENE SABİTLENMEZ: varyant renkleri `palette`ten devralınır
 * (CLAUDE.md tasarım sadakati — marka katmanı değiştirilebilir kalmalı).
 */
import { palette } from './tokens';
/** §2: tüm yollar 0 0 48 48 kutusunda tanımlıdır. */
export const BRAND_MARK_VIEWBOX = 48;
/**
 * §2 — TAM İŞARET (≥18 px): W zikzağı ortadan KIRIKTIR; kırıktan sonraki kol
 * aksan rengindedir. İki ayrı yol, iki ayrı renk.
 */
export const BRAND_MARK_PATH_LEFT = 'M9 15L16.5 33L24 19';
export const BRAND_MARK_PATH_RIGHT = 'M27.4 25.6L31.5 33L39 15';
/** §2 — BASİTLEŞTİRİLMİŞ W (<18 px): tek yol, aksan YOK, `miter` birleşim. */
export const BRAND_MARK_PATH_SIMPLE = 'M9 14L16.5 34L24 20L31.5 34L39 14';
/**
 * §2 kontur tablosu — `[çizim boyu px, viewBox birimi kontur]`.
 *
 * Kontur boya göre KALINLAŞIR (küçüldükçe optik telafi): 64 px'te 4,4 birim,
 * 18 px'te 6,6 birim. Ara boylar doğrusal ara değerle bulunur; tablo dışı
 * boylar uçlara sabitlenir.
 */
export const BRAND_MARK_STROKE_TABLE = [
    [18, 6.6],
    [20, 6.0],
    [24, 5.4],
    [26, 5.2],
    [40, 5.0],
    [56, 4.6],
    [64, 4.4],
];
/** §2 — basit W konturu: 16 px → 7 · 32 px → 6 · Android mono 24 dp → 6. */
export const BRAND_MARK_SIMPLE_STROKE_TABLE = [
    [16, 7],
    [32, 6],
];
/** §4 eşikleri: 18 ve üstü TAM çizim; basit W 12 px altına inmez. */
export const BRAND_MARK_FULL_MIN_PX = 18;
export const BRAND_MARK_SIMPLE_MIN_PX = 12;
/**
 * §4 kilit oranları.
 *
 * `wordmarkCapRatio`: işaret yüksekliği = wordmark cap yüksekliği × 1,55, yani
 * wordmark cap = kare × (1 / 1,55). `gapRatio`: kare-wordmark arası = kare × %27.
 * `clearSpaceRatio`: dört yönde kare kenarının ¼'ü.
 */
export const brandLock = {
    markToCap: 1.55,
    gapRatio: 0.27,
    clearSpaceRatio: 0.25,
    /** §4: yatay kilitte wordmark 13 px altına inmez, kare 24 px altına inmez. */
    minSquarePx: 24,
    minWordmarkPx: 13,
    /** §4: wordmark 700, harf aralığı −.02…−.025em (üretilen kilitlerde Roboto, 30.10.2026). */
    wordmarkWeight: '700',
    wordmarkLetterSpacing: '-0.022em',
};
/**
 * §3 — dört zemin varyantı. Ters çevirme kuralı: koyu bantta kare AÇILIR,
 * işaret ink olur (`dark`).
 */
export const brandMarkColors = {
    /** Açık zemin: e-posta açık, footer, giriş, landing, içerik alanları. */
    light: { square: palette.primary[500], mark: '#FFFFFF', accent: palette.accent[500] },
    /** Koyu bant (ink): TÜM üst bantlar, OG, QR kart imzası. */
    dark: { square: palette.neutral.bg, mark: palette.dark.bg, accent: palette.accent[600] },
    /** Karanlık tema: karanlık modda yüzey üstü (bant DEĞİL). */
    darkTheme: {
        square: palette.dark.primary,
        mark: palette.dark.bg,
        accent: palette.dark.accentTint,
    },
    /** Mono: kare yok, tek renk basit W (Android bildirim · damga · baskı). */
    mono: { square: null, mark: 'currentColor', accent: null },
};
function interpolate(table, size) {
    const first = table[0];
    const last = table[table.length - 1];
    if (size <= first[0])
        return first[1];
    if (size >= last[0])
        return last[1];
    for (let i = 1; i < table.length; i += 1) {
        const [x1, y1] = table[i];
        const [x0, y0] = table[i - 1];
        if (size <= x1)
            return round(y0 + ((y1 - y0) * (size - x0)) / (x1 - x0));
    }
    return last[1];
}
function round(value) {
    return Math.round(value * 100) / 100;
}
/** §2 tablosundan çizim boyuna karşılık gelen kontur (viewBox birimi). */
export function markStrokeWidth(sizePx) {
    return interpolate(BRAND_MARK_STROKE_TABLE, sizePx);
}
/** §2 basit W konturu; `mono` boyu 24 dp de bu tabloya düşer (6). */
export function simpleMarkStrokeWidth(sizePx) {
    return interpolate(BRAND_MARK_SIMPLE_STROKE_TABLE, sizePx);
}
/** §2 eşiği: 18 px altında tek yollu basit W çizilir. */
export function usesSimpleMark(sizePx) {
    return sizePx < BRAND_MARK_FULL_MIN_PX;
}
/**
 * Kare köşesi: `--r-2`..`--r-4` boya göre (§1). Değer viewBox birimine
 * çevrilir ki tek bir SVG her boyda token köşesini versin.
 */
export function squareRadius(sizePx) {
    const px = sizePx <= 26 ? 4 : sizePx <= 40 ? 6 : 8;
    return round((px * BRAND_MARK_VIEWBOX) / sizePx);
}
/**
 * Depodaki `brand/*.svg` dosyalarının ÜRETİCİSİ.
 *
 * Çıktı biçimi bilinçli olarak sabittir (tek satır girinti, sondaki satır
 * sonu): `brand-asset.guard.spec.ts` dosyayı BİREBİR karşılaştırır.
 */
export function brandMarkSvg(options) {
    const size = options.size ?? BRAND_MARK_VIEWBOX;
    const colors = brandMarkColors[options.variant];
    const simple = options.simple ?? usesSimpleMark(size);
    const withSquare = (options.square ?? true) && colors.square !== null;
    const box = BRAND_MARK_VIEWBOX;
    const lines = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box} ${box}" width="${box}" height="${box}" fill="none" role="img">`,
    ];
    if (withSquare) {
        lines.push(`  <rect width="${box}" height="${box}" rx="${squareRadius(size)}" fill="${colors.square}"/>`);
    }
    if (simple) {
        lines.push(`  <path d="${BRAND_MARK_PATH_SIMPLE}" stroke="${colors.mark}" stroke-width="${simpleMarkStrokeWidth(size)}" stroke-linejoin="miter"/>`);
    }
    else {
        const stroke = markStrokeWidth(size);
        lines.push(`  <path d="${BRAND_MARK_PATH_LEFT}" stroke="${colors.mark}" stroke-width="${stroke}" stroke-linecap="square"/>`);
        lines.push(`  <path d="${BRAND_MARK_PATH_RIGHT}" stroke="${colors.accent ?? colors.mark}" stroke-width="${stroke}" stroke-linecap="square"/>`);
    }
    lines.push('</svg>');
    return `${lines.join('\n')}\n`;
}
//# sourceMappingURL=brand-mark.js.map